'use client';

/**
 * PAGE FUSIONNÉE READ — exigence utilisateur II :
 * THE BOOK + READ (lecture verset par verset) + LISTEN + 114 SURATS
 * réunis sur une même page, avec barre d'ancres sticky, bannière dorée
 * et vitrine des 24 langues autour des 5 continents.
 */
import { useCallback, useEffect } from 'react';
import Image from 'next/image';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import LivreSection from './LivreSection';
import ReadSection from './ReadSection';
import ListenSection from './ListenSection';
import SurahsSection from './SurahsSection';
import BooksStrip from './BooksStrip';
import ContinentLanguages from './ContinentLanguages';
import { cn } from '@/lib/utils';

export type ReadAnchor = 'book' | 'read' | 'listen' | 'surahs';

export default function ReadPage({ anchor }: { anchor: ReadAnchor }) {
  const { uiLang } = useApp();
  const t = DICTS[uiLang];

  const scrollTo = useCallback((id: string) => {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Ancre initiale (ex. clic sur « Écouter » dans le menu → arrive sur la zone LISTEN)
  useEffect(() => {
    const id = anchor === 'book' ? 'rp-book' : `rp-${anchor}`;
    // petit délai : les sections enfants (selects, grilles) ont besoin d'un tick
    const t1 = setTimeout(() => scrollTo(id), 60);
    return () => clearTimeout(t1);
  }, [anchor, scrollTo]);

  const tabs = [
    { id: 'rp-book', label: t.readpage.tab_book },
    { id: 'rp-read', label: t.readpage.tab_read },
    { id: 'rp-listen', label: t.readpage.tab_listen },
    { id: 'rp-surahs', label: t.readpage.tab_surahs },
  ];

  return (
    <section aria-label={t.readpage.title}>
      {/* ===== Bannière dorée ===== */}
      <div className="relative overflow-hidden border-b border-[rgba(212,175,55,0.3)]">
        <Image
          src="/images/banners/read_v6.png"
          alt="READ — The Holy Quran"
          width={1600}
          height={640}
          priority
          unoptimized
          className="h-[280px] w-full object-cover sm:h-[360px]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,25,19,0.42),rgba(3,32,25,0.9))]" aria-hidden />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.4em] text-[#f0d98c]/90">{t.readpage.eyebrow}</p>
          <h1 className="font-serif-royal text-5xl font-bold sm:text-6xl">
            <span className="text-gold-gradient">{t.readpage.title}</span>
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-xs leading-relaxed text-[#cfe0cf]/90 sm:text-sm">
            {t.readpage.subtitle}
          </p>
        </div>
      </div>

      {/* ===== Barre d'ancres sticky (sous le header) ===== */}
      <nav
        className="sticky top-[56px] z-40 border-b border-[rgba(212,175,55,0.25)] bg-[rgba(3,32,25,0.96)] backdrop-blur-md"
        aria-label={t.readpage.title}
      >
        <div className="gold-scroll mx-auto flex max-w-4xl items-center gap-1 overflow-x-auto px-3 py-2 sm:justify-center sm:gap-2">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => scrollTo(tb.id)}
              className="flex shrink-0 items-center gap-1.5 rounded-full border border-[rgba(212,175,55,0.35)] px-3.5 py-1.5 text-[12px] font-bold text-[#f0d98c] transition-colors hover:bg-[rgba(212,175,55,0.12)] sm:px-4 sm:text-[13px]"
            >
              {tb.label}
            </button>
          ))}
        </div>
      </nav>

      {/* ===== 1. THE BOOK ===== */}
      <div id="rp-book" className="scroll-mt-24">
        <LivreSection onNavigate={(k) => scrollTo(`rp-${k}`)} />
      </div>

      {/* ===== 2. READ — lecture verset par verset ===== */}
      <div id="rp-read" className="scroll-mt-24 border-t border-[rgba(212,175,55,0.2)]">
        <ReadSection />
      </div>

      {/* ===== Vitrine : 24 langues / 5 continents ===== */}
      <ContinentLanguages />

      {/* ===== 3. LISTEN ===== */}
      <div id="rp-listen" className="scroll-mt-24 border-t border-[rgba(212,175,55,0.2)]">
        <ListenSection />
      </div>

      {/* ===== 4. 114 SURATS ===== */}
      <div id="rp-surahs" className={cn('scroll-mt-24 border-t border-[rgba(212,175,55,0.2)]')}>
        <SurahsSection />
      </div>

      {/* ===== 5. LES 28 LIVRES — sous les 114 sourates (exigence utilisateur III) ===== */}
      <BooksStrip />
    </section>
  );
}
